import { BASE_URL } from "../utils/constants";
import { getFileExtension } from "../utils/helperUtils";
import { callProtectedAPI, refreshAccessToken } from "./authServices";
import { v4 as uuidv4 } from 'uuid';
import { compressAndRenameFile } from "../utils/helperUtils";

/**
 * Generic Fetch Service for API Requests
 * @param {string} endpoint - API endpoint (relative to BASE_URL)
 * @param {string} errorMessage - Custom error message for failures
 */
const fetchData = async (endpoint, errorMessage) => {
    try {
        const response = await fetch(`${BASE_URL}/api/v1/storefronts/${endpoint}`);

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`${errorMessage} (Error ${response.statusText || ""}: ${errorText || response.statusText})`);
        }

        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        console.error(`Error fetching ${endpoint}:`, error);
        return { success: false, error: error.message };
    }
};

// Public storefront fetch methods
const fetchVendorDataService = slug => fetchData(`${slug}/`, "Storefront does not exist");
const fetchStorefrontReviews = slug => fetchData(`${slug}/reviews/`, "Unable to fetch storefront reviews");
const fetchPhotosService = endpoint => fetchData(endpoint, "Unable to fetch photos");

// Protected storefront fetch methods
const fetchStorefrontReviewsAuth = (slug, login, logout) =>
    callProtectedAPI(`/storefronts/${slug}/reviews/`, "GET", null, {}, login, logout);

const fetchMyStorefrontReviews = (slug, login, logout) =>
    callProtectedAPI(`/storefronts/${slug}/my_reviews/`, "GET", null, {}, login, logout);

const fetchMyStorefront = (login, logout) =>
    callProtectedAPI(`/storefronts/`, "GET", null, {}, login, logout);

const postStorefrontReview = (slug, rating, review_text, login, logout) => {
    const formData = new FormData();
    formData.append("rating", rating);
    formData.append("review_text", review_text);

    return callProtectedAPI(
        `/storefronts/${slug}/reviews/`,
        "POST",
        formData,
        { "Content-Type": "multipart/form-data" },
        login,
        logout
    );
};

const deleteMyStorefrontReview = (slug, login, logout) =>
    callProtectedAPI(`/storefronts/${slug}/my_reviews/`, "DELETE", null, {}, login, logout);

const createStoreFrontServiceAuth = async (data, login, logout, errorMsg = "Submission Error") => {
    try {

        if (data.hasOwnProperty('logo') && data.logo instanceof File) {
            const renamedFile = await compressAndRenameFile(data.logo, 'store_logo');
            data.logo = renamedFile;
        }

        var formData = new FormData();

        // Add each key-value pair to FormData
        Object.keys(data).forEach(key => {
            formData.append(key, data[key]);
        });

        const response = await callProtectedAPI(
            `/storefronts/`,
            "POST",
            formData,
            { 'Content-Type': 'multipart/form-data' },
            login,
            logout,
            errorMsg
        );
        if(response?.success) {
            refreshAccessToken(login, logout)
        }
        return response;
    } catch (error) {
        console.error("Error creating storefront:", error);
        return { success: false, error: error.message };
    }
};

const updateStoreFrontServiceAuth = async (slug, data, login, logout, errorMsg = "Update Error") => {
    try {
        if (data.hasOwnProperty('logo') && data.logo instanceof File) {
            const fileExtension = getFileExtension(data.logo);
            const uniqueFileName = `store_logo_${uuidv4()}.${fileExtension}`;
            const renamedFile = new File([data.logo], uniqueFileName, { type: data.logo.type });
            data.logo = renamedFile;
        }

        const formData = new FormData();

        Object.keys(data).forEach(key => {
            formData.append(key, data[key]);
        });

        const response = await callProtectedAPI(
            `/storefronts/${slug}/`,
            "PATCH",
            formData,
            { 'Content-Type': 'multipart/form-data' },
            login,
            logout,
            errorMsg
        );

        return response;
    } catch (error) {
        console.error("Error updating storefront:", error);
        return { success: false, error: error.message };
    }
};

const uploadStorePhotoServiceAuth = async (imageUris, slug, login, logout, errorMsg = "Store Image Submission Error") => {
    const URL = `/storefronts/${slug}/storefront_photos/`;

    // Prepare the form data
    const formData = new FormData();

    for (const imageFile of imageUris) {
        const renamedFile = await compressAndRenameFile(imageFile, 'store_photo');

        formData.append('storefront_photos', renamedFile);
    }

    try {
        const response = await callProtectedAPI(
            URL,
            "POST",
            formData,
            { 'Content-Type': 'multipart/form-data' },
            login,
            logout,
            errorMsg
        );

        return response;
    } catch (error) {
        console.log('uploadStorePhotoServiceAuth error:', error);
        return { success: false, error: error.message };
    }
}

const uploadMenuPhotoServiceAuth = async (imageUris, slug, login, logout, errorMsg = "Item Image Submission Error") => {
    const URL = `/storefronts/${slug}/menu_photos/`;

    const formData = new FormData();

    for (const imageFile of imageUris) {
        const renamedFile = await compressAndRenameFile(imageFile, 'menu_photo');

        formData.append('menu_photos', renamedFile);
    }

    try {
        const response = await callProtectedAPI(
            URL,
            "POST",
            formData,
            { 'Content-Type': 'multipart/form-data' },
            login,
            logout,
            errorMsg
        );

        return response;
    } catch (error) {
        console.log('uploadMenuPhotoServiceAuth error:', error);
        return { success: false, error: error.message };
    }
};


const deleteMenuPhotoServiceAuth = async (slug, menuPhotoId, login, logout, errorMsg = "Delete Item Photo Error") => {
    const URL = `/storefronts/${slug}/menu_photos/${menuPhotoId}/`;
    return await callProtectedAPI(
        URL,
        "DELETE",
        null,
        {},
        login,
        logout,
        errorMsg
    );
};

async function deleteStorePhotoServiceAuth(slug, photoId, login, logout, errorMsg = "Delete Store Photo Error") {

    const URL = `/storefronts/${slug}/storefront_photos/${photoId}/`;
    return await callProtectedAPI(
        URL,
        "DELETE",
        null,
        {},
        login,
        logout,
        errorMsg
    );
}

const deleteStoreFrontServiceAuth = async (slug, login, logout, errorMsg = "Delete Store Error") => {

    return await callProtectedAPI(
        `/storefronts/${slug}/`,
        "DELETE",
        null,
        {},
        login,
        logout,
        errorMsg
    );
};

const uploadPermitServiceAuth = async ({file, description}, slug, login, logout, errorMsg = "Permit Upload Error") => {

    if (!file || !description) {
        return { success: false, error: "File and description are required." };
    }

    const URL = `/storefronts/${slug}/permits/`;
    const formData = new FormData();

    const renamedFile = await compressAndRenameFile(file, 'permit');

    formData.append("file", renamedFile);
    formData.append("description", description);

    try {
        const response = await callProtectedAPI(
            URL,
            "POST",
            formData,
            { "Content-Type": "multipart/form-data" },
            login,
            logout,
            errorMsg
        );

        return response;
    } catch (error) {
        console.log("uploadPermitServiceAuth error:", error);
        return { success: false, error: error.message };
    }
};

const getStorefrontPermits = (slug, login, logout) =>
    callProtectedAPI(`/storefronts/${slug}/permits/`, "GET", null, {}, login, logout);

export {
    fetchVendorDataService,
    fetchStorefrontReviews,
    fetchPhotosService,
    fetchStorefrontReviewsAuth,
    fetchMyStorefrontReviews,
    fetchMyStorefront,
    postStorefrontReview,
    deleteMyStorefrontReview,
    createStoreFrontServiceAuth,
    uploadMenuPhotoServiceAuth,
    uploadStorePhotoServiceAuth,
    updateStoreFrontServiceAuth,
    deleteMenuPhotoServiceAuth,
    deleteStorePhotoServiceAuth,
    deleteStoreFrontServiceAuth,
    uploadPermitServiceAuth,
    getStorefrontPermits
};